import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import { useSnippets } from '../../hooks/useSnippets';
import Header from '../Common/Header';
import SnippetList from './SnippetList';
import SnippetEditor from '../Editor/SnippetEditor';
import { Plus, Search, Filter, Zap, BookOpen } from 'lucide-react';
import { addDemoSnippets } from '../../utils/demoSnippets';

const Dashboard = () => {
  const { user } = useAuth();
  const { snippets, loading, error, isOffline, addSnippet, editSnippet, removeSnippet, searchSnippets } = useSnippets();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [filteredSnippets, setFilteredSnippets] = useState([]);

  useEffect(() => {
    setFilteredSnippets(searchSnippets(searchQuery));
  }, [snippets, searchQuery, searchSnippets]);

  const handleCreateSnippet = () => {
    setSelectedSnippet(null);
    setIsEditorOpen(true);
  };

  const handleEditSnippet = (snippet) => {
    setSelectedSnippet(snippet);
    setIsEditorOpen(true);
  };

  const handleSaveSnippet = async (snippetData) => {
    try {
      if (selectedSnippet) {
        await editSnippet(selectedSnippet.id, snippetData);
      } else {
        await addSnippet(snippetData);
      }
      setIsEditorOpen(false);
      setSelectedSnippet(null);
    } catch (error) {
      console.error('Error saving snippet:', error);
    }
  };

  const handleDeleteSnippet = async (snippetId) => {
    if (window.confirm('Are you sure you want to delete this snippet?')) {
      try {
        await removeSnippet(snippetId);
      } catch (error) {
        console.error('Error deleting snippet:', error);
      }
    }
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setSelectedSnippet(null);
  };

  const handleAddDemoSnippets = async () => {
    try {
      await addDemoSnippets(addSnippet);
    } catch (error) {
      console.error('Error adding demo snippets:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading your snippets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome back, {user?.displayName?.split(' ')[0] || 'User'}!
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your code snippets and sync them across all your devices
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Snippets</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{snippets.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Search className="w-6 h-6 text-secondary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Languages</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {new Set(snippets.map(s => s.language)).size}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Filter className="w-6 h-6 text-accent" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Most Used</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.max(...snippets.map(s => s.usageCount || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search snippets by tag, title, or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          
          <div className="flex space-x-2">
            {snippets.length === 0 && (
              <button
                onClick={handleAddDemoSnippets}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Add Demo Snippets
              </button>
            )}
            
            <button
              onClick={handleCreateSnippet}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Snippet
            </button>
          </div>
        </div>

        {/* Error/Status Message */}
        {error && (
          <div className={`mb-6 rounded-lg p-4 ${isOffline 
            ? 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800' 
            : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
          }`}>
            <p className={`text-sm ${isOffline 
              ? 'text-yellow-700 dark:text-yellow-300' 
              : 'text-red-700 dark:text-red-300'
            }`}>
              {isOffline && '🔄 '}{error}
            </p>
          </div>
        )}

        {/* Snippets List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <SnippetList
            snippets={filteredSnippets}
            onEditSnippet={handleEditSnippet}
            onDeleteSnippet={handleDeleteSnippet}
            searchQuery={searchQuery}
          />
        </div>
      </div>

      {/* Editor Modal */}
      {isEditorOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <SnippetEditor
                snippet={selectedSnippet}
                onSave={handleSaveSnippet}
                onCancel={handleCloseEditor}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
